import { useState, useMemo, useRef, useEffect } from "react";
import { ChevronRight, BookOpen, Search, Star, Shield, Building2, Crown, Users, Quote, GraduationCap } from "lucide-react";
import { Input } from "./ui/input";
import { MANUALE_OPERATORE } from "../data/manualeOperatore";

const GROUP_ICONS = {
  "Staff Esecutivo": Crown,
  "Staff Comando": Shield,
  "Staff Supervisori": Star,
  "Staff di Pattuglia": Users,
  "Personale in Addestramento": GraduationCap,
  // Legacy fallback
  "Truppa": Users,
};

export default function ManualeOperatore() {
  const [activeId, setActiveId] = useState(MANUALE_OPERATORE[0]?.sections?.[0]?.id || null);
  const [query, setQuery] = useState("");
  const contentRef = useRef(null);

  const filteredManual = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return MANUALE_OPERATORE;
    const matchSection = (s) => {
      const hay = [
        s.title,
        s.body || "",
        s.quote || "",
        ...(s.tiers || []).flatMap((t) => [t.label, t.description]),
        ...(s.ranks || []).flatMap((r) => [r.group, ...r.items]),
        ...(s.groups || []).flatMap((g) => [
          g.label,
          ...(g.bureaus || []).flatMap((b) => [b.name, b.short, b.description]),
        ]),
        ...(s.blocks || []).flatMap((b) => [
          b.text || "",
          ...(b.items || []),
          ...(b.headers || []),
          ...(b.rows || []).flat(),
        ]),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    };
    return MANUALE_OPERATORE.map((ch) => ({ ...ch, sections: ch.sections.filter(matchSection) }))
      .filter((ch) => ch.sections.length > 0);
  }, [query]);

  const activeSection = useMemo(() => {
    for (const ch of MANUALE_OPERATORE) {
      const s = ch.sections.find((x) => x.id === activeId);
      if (s) return { chapter: ch, section: s };
    }
    return null;
  }, [activeId]);

  const handlePick = (id) => {
    setActiveId(id);
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  };

  useEffect(() => {
    if (!query) return;
    const firstVisibleId = filteredManual[0]?.sections?.[0]?.id;
    if (firstVisibleId && !filteredManual.some((c) => c.sections.some((s) => s.id === activeId))) {
      setActiveId(firstVisibleId);
    }
  }, [query, filteredManual, activeId]);

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6 lg:h-[calc(100vh-13rem)]" data-testid="manuale-operatore">
      <aside className="space-y-3 lg:h-full lg:overflow-y-auto pr-1 manuale-scroll">
        <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-xl p-3 flex items-center gap-2">
          <Search className="h-4 w-4 text-sheriff-gold/60 ml-1" />
          <Input
            data-testid="manuale-search"
            placeholder="Cerca nel manuale…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 bg-transparent px-0 text-sm h-8"
          />
        </div>

        <nav className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-3 space-y-3" aria-label="Indice manuale">
          {filteredManual.length === 0 && (
            <div className="text-center text-[#B8A269] py-4 text-sm" data-testid="manuale-no-results">
              Nessun risultato per &quot;{query}&quot;
            </div>
          )}
          {filteredManual.map((ch) => (
            <div key={ch.id}>
              <div className="px-2 py-1.5 mb-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60">
                  Capitolo {ch.number}
                </div>
                <div className="font-heading font-bold text-sm text-[#F5E6B8] leading-tight">
                  {ch.title}
                </div>
                {ch.subtitle && (
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#8B7B49] mt-0.5">{ch.subtitle}</div>
                )}
              </div>
              <ul className="space-y-0.5">
                {ch.sections.map((s) => {
                  const isActive = s.id === activeId;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => handlePick(s.id)}
                        data-testid={`manuale-toc-${s.id}`}
                        className={`w-full text-left px-2.5 py-2 rounded-lg flex items-start gap-2 text-sm transition-colors group ${
                          isActive
                            ? "bg-sheriff-gold/15 text-sheriff-gold border-l-2 border-sheriff-gold"
                            : "text-[#D4C480] hover:text-sheriff-gold hover:bg-sheriff-gold/5 border-l-2 border-transparent"
                        }`}
                      >
                        <span className="font-mono text-xs font-bold opacity-70 shrink-0">{s.number}</span>
                        <span className="leading-tight flex-1">{s.title}</span>
                        {isActive && <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <article
        ref={contentRef}
        className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6 sm:p-8 lg:self-start lg:max-h-full lg:overflow-y-auto manuale-scroll"
        data-testid="manuale-content"
      >
        {activeSection ? (
          <SectionRenderer chapter={activeSection.chapter} section={activeSection.section} />
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-16 text-[#B8A269]">
            <BookOpen className="h-10 w-10 mb-3 opacity-50" />
            <p>Seleziona una sezione dal menu</p>
          </div>
        )}
      </article>
    </div>
  );
}

function SectionRenderer({ chapter, section }) {
  return (
    <div className="space-y-5">
      <header className="border-b border-sheriff-gold/15 pb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-sheriff-gold/70 mb-1">
          Capitolo {chapter.number} · {chapter.title}
        </div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#F5E6B8] leading-tight">
          <span className="font-mono text-sheriff-gold mr-3">{section.number}</span>
          {section.title}
        </h2>
        {section.subtitle && (
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#8B7B49] font-bold">
            {section.subtitle}
          </div>
        )}
      </header>

      {section.body && (
        <div className="text-[#E8D89A] leading-relaxed whitespace-pre-wrap text-[15px]">
          {section.body}
        </div>
      )}

      {section.type === "motto" && section.quote && <MottoCard quote={section.quote} />}
      {section.type === "hierarchy" && <HierarchyBlock tiers={section.tiers || []} ranks={section.ranks || []} />}
      {section.type === "divisions" && <DivisionsBlock groups={section.groups || []} />}

      {Array.isArray(section.blocks) && section.blocks.length > 0 && (
        <BlocksRenderer blocks={section.blocks} />
      )}

      <SectionSignature chapterNumber={chapter.number} sectionNumber={section.number} />
    </div>
  );
}

function SectionSignature({ chapterNumber, sectionNumber }) {
  return (
    <div className="pt-8 mt-2">
      <div className="relative rounded-xl border border-sheriff-gold/20 bg-gradient-to-br from-[#0F2620] via-[#102A22] to-[#0A1F18] overflow-hidden">
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 50%, #D4AF37 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="relative flex items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full border border-sheriff-gold/40 bg-sheriff-gold/10 flex items-center justify-center shrink-0">
              <Star className="h-4 w-4 text-sheriff-gold fill-sheriff-gold/30" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-sheriff-gold text-sm italic leading-none">
                A Tradition of Service
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#8B7B49] mt-1">
                Los Santos County Sheriff&apos;s Department · Est. 1850
              </div>
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
            <div className="font-mono text-[10px] tracking-[0.2em] text-sheriff-gold/70 font-bold">
              § {sectionNumber}
            </div>
            <div className="font-mono text-[9px] text-[#8B7B49]">
              MANUALE OPERATORE · CAP. {chapterNumber}
            </div>
          </div>
        </div>
        {/* Bottom gold strip */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-sheriff-gold/40 to-transparent" />
      </div>
    </div>
  );
}

function BlocksRenderer({ blocks }) {
  return (
    <div className="space-y-4">
      {blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  );
}

function Block({ block }) {
  if (block.type === "paragraph") {
    return <p className="text-[#E8D89A] leading-relaxed text-[15px] whitespace-pre-wrap">{block.text}</p>;
  }
  if (block.type === "subhead") {
    return (
      <h3 className="font-heading font-bold text-sheriff-gold text-base uppercase tracking-wide pt-2">
        {block.text}
      </h3>
    );
  }
  if (block.type === "bullets") {
    return (
      <ul className="space-y-1.5 ml-1">
        {(block.items || []).map((it, i) => (
          <li key={i} className="flex gap-2 text-[#E8D89A] text-[15px] leading-relaxed">
            <span className="text-sheriff-gold mt-1.5 shrink-0">•</span>
            <span className="whitespace-pre-wrap">{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "numbered") {
    return (
      <ol className="space-y-1.5 ml-1">
        {(block.items || []).map((it, i) => (
          <li key={i} className="flex gap-2 text-[#E8D89A] text-[15px] leading-relaxed">
            <span className="font-mono text-sheriff-gold font-bold shrink-0 min-w-[1.5rem]">{i + 1}.</span>
            <span className="whitespace-pre-wrap">{it}</span>
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === "callout") {
    const tone = block.tone || "gold";
    const cls = tone === "rose"
      ? "bg-rose-500/10 border-rose-500/30 text-rose-200"
      : tone === "amber"
      ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
      : tone === "emerald"
      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
      : "bg-sheriff-gold/10 border-sheriff-gold/30 text-[#F5E6B8]";
    return (
      <div className={`border rounded-xl p-4 text-[14px] leading-relaxed whitespace-pre-wrap ${cls}`}>
        {block.title && <div className="font-heading font-bold mb-1">{block.title}</div>}
        {block.text}
      </div>
    );
  }
  if (block.type === "table") {
    return (
      <div className="overflow-x-auto rounded-xl border border-sheriff-gold/20">
        <table className="w-full text-sm">
          {block.headers && (
            <thead className="bg-sheriff-gold/10">
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left px-3 py-2 font-heading text-sheriff-gold uppercase tracking-wide text-xs border-b border-sheriff-gold/20">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {(block.rows || []).map((row, i) => (
              <tr key={i} className="border-b border-sheriff-gold/10 last:border-b-0 hover:bg-sheriff-gold/5">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2 text-[#E8D89A] align-top whitespace-pre-wrap">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === "kv") {
    return (
      <div className="grid sm:grid-cols-2 gap-2">
        {(block.items || []).map((it, i) => (
          <div key={i} className="bg-[#0F2620] border border-sheriff-gold/20 rounded-lg px-3 py-2 flex items-baseline gap-2">
            <span className="font-mono text-sheriff-gold font-bold text-sm shrink-0">{it.k}</span>
            <span className="text-[#E8D89A] text-sm">{it.v}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function MottoCard({ quote }) {
  return (
    <div className="relative bg-gradient-to-br from-sheriff-gold/15 via-[#0F2620] to-[#0A1F18] border border-sheriff-gold/40 rounded-2xl p-8 text-center overflow-hidden">
      <Quote className="absolute top-4 left-4 h-8 w-8 text-sheriff-gold/20" />
      <Quote className="absolute bottom-4 right-4 h-8 w-8 text-sheriff-gold/20 rotate-180" />
      <div className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-sheriff-gold italic mb-1">
        &ldquo;{quote}&rdquo;
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8B7B49]">
        Motto Ufficiale · LSSD
      </div>
    </div>
  );
}

function HierarchyBlock({ tiers, ranks }) {
  return (
    <div className="space-y-6">
      {tiers.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3">
          {tiers.map((t) => (
            <div key={t.label} className="bg-[#0F2620] border border-sheriff-gold/25 rounded-xl p-4" data-testid={`tier-${t.label}`}>
              <div className="font-heading font-bold text-sheriff-gold text-base mb-2">{t.label}</div>
              <p className="text-xs text-[#D4C480] leading-relaxed">{t.description}</p>
            </div>
          ))}
        </div>
      )}
      {ranks.length > 0 && (
        <div className="space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-sheriff-gold/70">Scala dei Gradi</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {ranks.map((g) => {
              const Icon = GROUP_ICONS[g.group] || Shield;
              return (
                <div key={g.group} className="bg-[#0F2620] border border-sheriff-gold/25 rounded-xl p-4" data-testid={`rank-group-${g.group}`}>
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-sheriff-gold/15">
                    <Icon className="h-4 w-4 text-sheriff-gold" />
                    <div className="font-heading font-bold text-[#F5E6B8] text-sm uppercase tracking-wide">{g.group}</div>
                  </div>
                  <ol className="space-y-1.5">
                    {g.items.map((rank, i) => (
                      <li key={rank} className="flex items-center gap-2 text-sm text-[#E8D89A]">
                        <span className="font-mono text-[10px] text-sheriff-gold/60 w-5">{String(i + 1).padStart(2, "0")}</span>
                        <span>{rank}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DivisionsBlock({ groups }) {
  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <div key={g.label} data-testid={`division-group-${g.label}`}>
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-sheriff-gold" />
            <h3 className="font-heading font-bold text-lg text-sheriff-gold uppercase tracking-wide">{g.label}</h3>
            <div className="flex-1 h-px bg-sheriff-gold/15" />
            <span className="font-mono text-[10px] text-[#8B7B49]">{g.bureaus.length} bureau</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {g.bureaus.map((b) => (
              <div key={b.name} className="bg-[#0F2620] border border-sheriff-gold/25 rounded-xl p-4 hover:border-sheriff-gold/50 transition-colors" data-testid={`bureau-${b.short}`}>
                <div className="flex items-start gap-3 mb-2">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 border border-sheriff-gold/40 bg-sheriff-gold/10">
                    <span className="font-mono font-black text-[11px] text-sheriff-gold">{b.short}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading font-bold text-[#F5E6B8] text-sm leading-tight">{b.name}</h4>
                  </div>
                </div>
                <p className="text-xs text-[#D4C480] leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
